import { typeMod, typeLabel } from "../data/types.ts";
import { speciesById } from "../data/species.ts";
import { ITEMS, type ItemId } from "../data/items.ts";
import { MOVES, statPairs, type MoveDef } from "../data/moves.ts";
import { STATUS_META, statusCatchMul, type StatusId } from "../data/status.ts";
import type { Rng } from "../engine/rng.ts";
import { EARLY_LEVEL, grantExp, isFaint, maxHp, moveDef, statOf, usableMoves, type GrowthResult, type Mon } from "./mon.ts";

export interface BattleMon {
  mon: Mon;
  atk: number;
  def: number;
  spd: number;
  flash: number;
  shake: number;
  shownHp: number;
  faint: number;
  flinched: boolean;
  sureHit: boolean;
  /** Battle-only condition; a fresh wrapper (switch, new battle) starts clean. */
  status: StatusId | null;
  /** AFK: turns left to skip. Frost: turns left before a forced thaw. FUD: current 16ths. */
  statusTurns: number;
}

export function wrapBattle(mon: Mon): BattleMon {
  return {
    mon,
    atk: 0,
    def: 0,
    spd: 0,
    flash: 0,
    shake: 0,
    shownHp: mon.hp,
    faint: 0,
    flinched: false,
    sureHit: false,
    status: null,
    statusTurns: 0,
  };
}

function stageMul(s: number): number {
  const c = Math.max(-6, Math.min(6, s));
  return c >= 0 ? (2 + c) / 2 : 2 / (2 - c);
}

export function effStat(b: BattleMon, key: "atk" | "def" | "spd"): number {
  const v = Math.max(1, Math.floor(statOf(b.mon, key) * stageMul(b[key])));
  return key === "spd" && b.status === "lag" ? Math.max(1, Math.floor(v / 2)) : v;
}

/** Whether a condition can land: one at a time, and each type shrugs off its own element. */
export function canAfflict(b: BattleMon, s: StatusId): boolean {
  if (isFaint(b.mon) || b.status) return false;
  const immune = STATUS_META[s].immune;
  return !immune || !speciesById(b.mon.speciesId).types.includes(immune);
}

/** Set a condition (caller checked canAfflict). AFK rolls its nap length here. */
export function afflict(rng: Rng, b: BattleMon, s: StatusId): string {
  b.status = s;
  b.statusTurns = s === "afk" ? rng.int(1, 3) : s === "frost" ? 2 : s === "fud" ? 1 : 0;
  return `${b.mon.nick} is ${STATUS_META[s].verb}!`;
}

export interface StatusCheck {
  skip: boolean;
  notes: string[];
}

/** Before acting: AFK naps, ice blocks and lag spikes can cost the turn. */
export function beforeMove(rng: Rng, b: BattleMon): StatusCheck {
  const nick = b.mon.nick;
  switch (b.status) {
    case "afk":
      if (b.statusTurns > 0) {
        b.statusTurns -= 1;
        return { skip: true, notes: [`${nick} is AFK. Zzz...`] };
      }
      b.status = null;
      return { skip: false, notes: [`${nick} logs back in!`] };
    case "frost":
      if (b.statusTurns <= 0 || rng.chance(0.3)) {
        b.status = null;
        b.statusTurns = 0;
        return { skip: false, notes: [`${nick} thaws out!`] };
      }
      b.statusTurns -= 1;
      return { skip: true, notes: [`${nick} is frozen solid!`] };
    case "lag":
      if (rng.chance(0.25)) return { skip: true, notes: [`${nick} lags out! Lost the turn.`] };
      return { skip: false, notes: [] };
    default:
      return { skip: false, notes: [] };
  }
}

/** End of turn: scorch burns a flat 1/12, FUD grows by 1/16 every turn it lingers. */
export function endOfTurn(b: BattleMon): { dmg: number; note: string } | null {
  if (isFaint(b.mon) || (b.status !== "scorch" && b.status !== "fud")) return null;
  const hp = maxHp(b.mon);
  const want = b.status === "scorch" ? Math.floor(hp / 12) : Math.floor((hp * Math.min(8, b.statusTurns)) / 16);
  const dmg = Math.min(b.mon.hp, Math.max(1, want));
  b.mon.hp -= dmg;
  if (b.status === "fud") b.statusTurns += 1;
  return { dmg, note: b.status === "scorch" ? `${b.mon.nick} is hurt by the scorch.` : `${b.mon.nick} is eaten by FUD.` };
}

export function clearLoweredStats(b: BattleMon): boolean {
  let changed = false;
  for (const stat of ["atk", "def", "spd"] as const) {
    if (b[stat] < 0) {
      b[stat] = 0;
      changed = true;
    }
  }
  return changed;
}

/** Offer a guard break that the living crew can actually perform with remaining PP. */
export function guardBreakHint(you: BattleMon, foe: BattleMon, party: Mon[]): string | null {
  if (foe.def <= 0) return null;
  const candidates = [you.mon, ...party.filter(mon => mon.uid !== you.mon.uid)];
  for (const mon of candidates) {
    if (isFaint(mon)) continue;
    const slot = usableMoves(mon).find(slot => {
      const fx = moveDef(slot).fx;
      return fx?.kind === "stat" && fx.target === "foe" && fx.stat === "def" && fx.stages < 0;
    });
    if (slot) return mon === you.mon ? `Guard: try ${moveDef(slot).name}.` : `${mon.nick.slice(0, 12)} can lower foe DEF.`;
  }
  return null;
}

/** Existing drops can help from neutral stages too; name only the active partner's ready moves. */
export function statDropHint(you: BattleMon, foe: BattleMon): string | null {
  if (isFaint(you.mon) || isFaint(foe.mon)) return null;
  for (const stat of ["def", "atk"] as const) {
    if (foe[stat] <= -2) continue;
    const slot = usableMoves(you.mon).find(slot => {
      const fx = moveDef(slot).fx;
      return fx?.kind === "stat" && fx.target === "foe" && fx.stat === stat && fx.stages < 0;
    });
    if (slot) return `Lower ${stat.toUpperCase()}: ${moveDef(slot).name}.`;
  }
  return null;
}

/** Remaining support PP must not hide an attack-ready reserve during a long duel. */
export function attackPpHint(you: BattleMon, party: Mon[]): string | null {
  if (isFaint(you.mon)) return null;
  const slots = usableMoves(you.mon);
  if (slots.some(slot => moveDef(slot).power > 0)) return null;
  const reserve = party.find(mon => mon.uid !== you.mon.uid && !isFaint(mon)
    && usableMoves(mon).some(slot => moveDef(slot).power > 0));
  if (reserve) return `No attack PP: ${reserve.nick.slice(0, 12)}.`;
  return slots.length ? "No attack PP. Status moves remain." : "No PP: Flail Post costs HP.";
}

/** Who acts first this turn: higher move priority, then effective speed; null on a dead heat. */
export function actsFirst(a: BattleMon, am: MoveDef, b: BattleMon, bm: MoveDef): boolean | null {
  const pa = am.priority ?? 0, pb = bm.priority ?? 0;
  if (pa !== pb) return pa > pb;
  const sa = effStat(a, "spd"), sb = effStat(b, "spd");
  return sa === sb ? null : sa > sb;
}

export interface StrikeResult {
  hit: boolean;
  dmg: number;
  crit: boolean;
  mod: number;
  notes: string[];
  faint: boolean;
}

export function pickWildMove(rng: Rng, me: BattleMon, foe: BattleMon, previousMove?: MoveDef): MoveDef {
  const slots = usableMoves(me.mon);
  const available = (slots.length ? slots : [{ id: "flailPost", pp: 1 }]).map((s) => moveDef(s));
  // Give setup/recovery a reply beat before another support turn. Status-only
  // kits and exhausted attack PP still use their legal moves (or the fallback).
  const attacks = available.filter((mv) => mv.power > 0);
  const defs = previousMove?.power === 0 && attacks.length ? attacks : available;
  const foeTypes = speciesById(foe.mon.speciesId).types;
  const myTypes = speciesById(me.mon.speciesId).types;
  const hpRatio = me.mon.hp / maxHp(me.mon);
  const scored = defs.map((mv) => {
    const accuracy = me.sureHit ? 1 : mv.acc / 100;
    let s = mv.power * accuracy * typeMod(mv.type, foeTypes) * (myTypes.includes(mv.type) ? 1.25 : 1);
    const fx = mv.fx;
    if (fx?.kind === "heal") s += Math.min(fx.pct, 1 - hpRatio) * (hpRatio < 0.35 ? 300 : 100);
    // A cure is worth most with a condition to clear (FUD grows every turn); without one it is a small heal.
    if (fx?.kind === "cure") s += (me.status === "fud" ? 80 : me.status ? 55 : 0) + Math.min(fx.pct, 1 - hpRatio) * (hpRatio < 0.35 ? 300 : 100);
    if (fx?.kind === "stats") {
      const room = statPairs(fx).some(([k, v]) => (v > 0 ? me[k] < 2 : me[k] > -2));
      if (room) s += hpRatio > 0.5 ? 46 : 8;
    }
    if (fx?.kind === "drain") s += (1 - hpRatio) * 30;
    if (fx?.kind === "recoil" && hpRatio < 0.3) s *= 0.65;
    if (fx?.kind === "stat") {
      const target = fx.target === "self" ? me : foe;
      const useful = fx.stages > 0 ? target[fx.stat] < 2 : target[fx.stat] > -2;
      const speedUseful = fx.stat !== "spd" || effStat(me, "spd") <= effStat(foe, "spd");
      if (useful && speedUseful) s += hpRatio > 0.5 ? 40 : 8;
    }
    if (fx?.kind === "surehit" && !me.sureHit && defs.some((m) => m.power > 0 && m.acc < 100)) s += 30;
    if (fx?.kind === "multi") s *= (fx.min + fx.max) / 2;
    if ((mv.priority ?? 0) > 0 && mv.power > 0 && effStat(me, "spd") < effStat(foe, "spd")) s += 12;
    if (fx?.kind === "status") {
      const lands = canAfflict(foe, fx.status);
      if (mv.power === 0) s = lands ? (hpRatio > 0.4 ? 48 : 16) : 0;
      else if (lands) s += 30 * fx.chance;
    }
    return { mv, s };
  });
  // Keep some variety, but never roll a full-HP heal or spent setup if a useful move exists.
  // Green memelings (below EARLY_LEVEL) have not learned what works yet: any useful move will do.
  const useful = scored.filter(({ s }) => s > 0);
  const choices = useful.length ? useful : scored;
  const best = Math.max(...choices.map(({ s }) => s));
  const sharp = rng.chance(0.82) && me.mon.level >= EARLY_LEVEL;
  return rng.pick(sharp ? choices.filter(({ s }) => s === best) : choices).mv;
}

export function useMove(rng: Rng, user: BattleMon, foe: BattleMon, mv: MoveDef): StrikeResult {
  const notes: string[] = [];
  const slot = user.mon.moves.find((s) => s.id === mv.id);
  if (slot && slot.pp > 0) slot.pp -= 1;

  const acc = user.sureHit ? 100 : mv.acc;
  if (user.sureHit) user.sureHit = false;
  const hit = rng.int(1, 100) <= acc;
  if (!hit) {
    notes.push("MISS! It sails wide.");
    return { hit: false, dmg: 0, crit: false, mod: 1, notes, faint: false };
  }

  let dmg = 0;
  let crit = false;
  let mod = 1;
  if (mv.power > 0) {
    const atk = effStat(user, "atk");
    const def = effStat(foe, "def");
    const lv = user.mon.level;
    const base = Math.floor(((2 * lv) / 5 + 2) * mv.power * (atk / Math.max(1, def))) / 50 + 2;
    const userTypes = speciesById(user.mon.speciesId).types;
    const stab = userTypes.includes(mv.type) ? 1.25 : 1;
    mod = typeMod(mv.type, speciesById(foe.mon.speciesId).types);
    const critChance = 0.06 + mv.crit * 0.08;
    crit = rng.chance(critChance);
    const hits = mv.fx?.kind === "multi" ? rng.int(mv.fx.min, mv.fx.max) : 1;
    let landed = 0;
    for (let n = 0; n < hits && foe.mon.hp > 0; n++) {
      const spread = rng.float(0.85, 1);
      const hit = Math.min(foe.mon.hp, Math.max(1, Math.floor(base * stab * mod * (crit ? 1.5 : 1) * spread)));
      foe.mon.hp = Math.max(0, foe.mon.hp - hit);
      dmg += hit;
      landed += 1;
    }
    foe.flash = 70;
    foe.shake = crit ? 160 : 110;
    if (crit) notes.push("CRITICAL HIT!");
    const lab = typeLabel(mod);
    if (lab) notes.push(lab);
    if (hits > 1) notes.push(`Hit ${landed} time${landed === 1 ? "" : "s"}!`);
  }

  const fx = mv.fx;
  if (fx) {
    if (fx.kind === "stat") {
      const t = fx.target === "self" ? user : foe;
      const before = t[fx.stat];
      t[fx.stat] = Math.max(-6, Math.min(6, before + fx.stages));
      const who = fx.target === "self" ? user.mon.nick : foe.mon.nick;
      const dir = fx.stages > 0 ? "up" : "down";
      notes.push(`${who} ${fx.stat.toUpperCase()} ${before === t[fx.stat] ? "at limit" : dir}.`);
    } else if (fx.kind === "heal") {
      const add = Math.max(1, Math.floor(maxHp(user.mon) * fx.pct));
      user.mon.hp = Math.min(maxHp(user.mon), user.mon.hp + add);
      notes.push(`${user.mon.nick} sips the bit.`);
    } else if (fx.kind === "stats") {
      const moved = statPairs(fx).map(([k, v]) => {
        const before = user[k];
        user[k] = Math.max(-6, Math.min(6, before + v));
        return user[k] === before ? null : `${k.toUpperCase()} ${v > 0 ? "up" : "down"}`;
      }).filter(Boolean);
      notes.push(moved.length ? `${user.mon.nick} ${moved.join(", ")}.` : `${user.mon.nick} is already at the limit.`);
    } else if (fx.kind === "cure") {
      if (user.status) {
        user.status = null;
        user.statusTurns = 0;
        notes.push(`${user.mon.nick} shakes it off!`);
      }
      const add = Math.max(1, Math.floor(maxHp(user.mon) * fx.pct));
      user.mon.hp = Math.min(maxHp(user.mon), user.mon.hp + add);
      notes.push(`${user.mon.nick} feels brand new.`);
    } else if (fx.kind === "drain") {
      const drain = Math.max(1, Math.floor(dmg * fx.pct));
      user.mon.hp = Math.min(maxHp(user.mon), user.mon.hp + drain);
      notes.push("It siphons the joke.");
    } else if (fx.kind === "recoil") {
      const rec = Math.max(1, Math.floor(Math.max(dmg, 1) * fx.pct));
      user.mon.hp = Math.max(0, user.mon.hp - rec);
      notes.push(`${user.mon.nick} feels the bounce.`);
    } else if (fx.kind === "flinch" && rng.chance(fx.chance)) {
      foe.flinched = true;
    } else if (fx.kind === "surehit") {
      user.sureHit = true;
      notes.push("The next swing is locked.");
    } else if (fx.kind === "status") {
      if (canAfflict(foe, fx.status)) {
        // Damaging moves roll their rider; pure status moves already passed the accuracy roll.
        if (mv.power === 0 || rng.chance(fx.chance)) notes.push(afflict(rng, foe, fx.status));
      } else if (mv.power === 0) {
        notes.push(foe.status ? `${foe.mon.nick} is already ${STATUS_META[foe.status].verb}.` : "It has no effect.");
      }
    }
  }
  // Fire melts ice: any BURN hit that connects thaws a frozen target.
  if (dmg > 0 && mv.type === "burn" && foe.status === "frost" && !isFaint(foe.mon)) {
    foe.status = null;
    foe.statusTurns = 0;
    notes.push(`${foe.mon.nick} thaws out!`);
  }

  return { hit: true, dmg, crit, mod, notes, faint: isFaint(foe.mon) };
}

export interface CatchRoll {
  shakes: number;
  caught: boolean;
}

function catchThreshold(wild: Mon, item: ItemId, status: StatusId | null = null): number {
  if (isFaint(wild) || ITEMS[item].kind !== "capsule") return -1;
  const health = Math.max(0, Math.min(1, wild.hp / maxHp(wild)));
  const base = speciesById(wild.speciesId).catch / 100;
  const target = Math.max(0.02, Math.min(0.95, base * (1 - 0.7 * health) * (ITEMS[item].catchMul ?? 1) * statusCatchMul(status)));
  // Three independent latches should produce the intended overall probability,
  // rather than cubing an already-low catch chance. Round down to match the 8-bit roll.
  return Math.floor(Math.cbrt(target) * 256) - 1;
}

/** Exact chance after latch quantization, shared by the preview and actual throw. */
export function catchChance(wild: Mon, item: ItemId, status: StatusId | null = null): number {
  return ((catchThreshold(wild, item, status) + 1) / 256) ** 3;
}

export function throwCapsule(rng: Rng, wild: Mon, item: ItemId, status: StatusId | null = null): CatchRoll {
  const threshold = catchThreshold(wild, item, status);
  if (threshold < 0) return { shakes: 0, caught: false };
  let shakes = 0;
  let caught = true;
  for (let i = 0; i < 3; i++) {
    if (rng.int(0, 255) > threshold) {
      caught = false;
      break;
    }
    shakes += 1;
  }
  if (caught) shakes = 3;
  return { shakes, caught };
}

export function bountyFor(wild: Mon, caught: boolean): number {
  const b = speciesById(wild.speciesId).bounty;
  return (caught ? 30 : 20) + wild.level * 4 + b;
}

export function expFor(wild: Mon, recipientLevel = wild.level, trainer = false): number {
  // Same-level wins take about two fights per level. Deep/evolved foes pay more;
  // returning to beginner grass yields less, while a trailing crew catches up.
  const base = 12 + wild.level * 4 + Math.floor(speciesById(wild.speciesId).bounty / 2);
  const gap = Math.max(0.25, Math.min(1.5, 1 + (wild.level - recipientLevel) * 0.08));
  return Math.max(1, Math.floor(base * gap * (trainer ? 1.25 : 1)));
}

export function applyWinExp(lead: Mon, wild: Mon, trainer = false, share = 1): GrowthResult & { exp: number } {
  const exp = Math.max(1, Math.floor(expFor(wild, lead.level, trainer) * share));
  const r = grantExp(lead, exp);
  return { ...r, exp };
}

/** Visual faint: the creature sinks and fades over this long after its HP bar empties. */
export const FAINT_MS = 380;

export function tickShown(b: BattleMon, reduced: boolean, dt = 1000 / 60): void {
  if (reduced) {
    b.shownHp = b.mon.hp;
    b.flash = b.shake = 0;
    b.faint = isFaint(b.mon) ? 1 : 0;
    return;
  }
  const elapsed = Math.max(0, dt);
  const hpTravel = Math.abs(b.shownHp - b.mon.hp) * 220 / maxHp(b.mon);
  const step = maxHp(b.mon) * elapsed / 220;
  if (b.shownHp > b.mon.hp) b.shownHp = Math.max(b.mon.hp, b.shownHp - step);
  else if (b.shownHp < b.mon.hp) b.shownHp = Math.min(b.mon.hp, b.shownHp + step);
  b.flash = Math.max(0, b.flash - elapsed);
  b.shake = Math.max(0, b.shake - elapsed);
  // Only time after the HP bar reaches zero belongs to the faint pose.
  b.faint = isFaint(b.mon) && b.shownHp === 0 ? Math.min(1, b.faint + Math.max(0, elapsed - hpTravel) / FAINT_MS) : 0;
}

export { MOVES };
